import { useState, useEffect } from 'react';
import { FiMenu, FiMoon, FiSun, FiX } from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import logoLight from '../../images/logo-dark.svg';
import logoDark from '../../images/logo-light.svg';
import { motion, AnimatePresence } from 'framer-motion';

const AppHeader = () => {
	const [showMenu, setShowMenu] = useState(false);
	const { theme, toggleTheme, mounted } = useTheme();
	const location = useLocation();

	useEffect(() => {
		setShowMenu(false);
	}, [location]);

	function toggleMenu() {
		setShowMenu(!showMenu);
	}

	const navItems = [
		{ path: '/', label: 'Home' },
		{ path: '/projects', label: 'Projects' },
		{ path: '/about', label: 'About' },
		{ path: '/contact', label: 'Contact' }
	];

	// Don't render theme-dependent content until mounted
	if (!mounted) {
		return (
			<header className="fixed top-0 left-0 right-0 z-50 glass-effect transition-all duration-500">
				<div className="absolute inset-0 bg-white/5" />
				<nav className="container mx-auto px-4 relative z-10">
					<div className="flex items-center justify-between h-20 lg:h-24">
						<Link to="/" className="flex items-center group">
							<div className="relative">
								<div className="absolute inset-0 bg-blue-500 rounded-full blur-xl opacity-0 group-hover:opacity-30 transition-opacity duration-300" />
								<img src={logoLight} className="h-16 lg:h-20 relative z-10" alt="Logo" />
							</div>
						</Link>
						<div className="hidden lg:flex items-center space-x-1">
							{navItems.map((item) => (
								<Link
									key={item.path}
									to={item.path}
									className="relative px-6 py-3 text-sm font-medium transition-all duration-300 rounded-2xl group overflow-hidden text-gray-700 hover:text-blue-600 hover:bg-white/50"
								>
									<span className="relative z-10">{item.label}</span>
								</Link>
							))}
						</div>
						<div className="flex items-center space-x-3">
							<div className="w-11 h-11 bg-gray-200 rounded-2xl animate-pulse"></div>
							<div className="w-11 h-11 bg-gray-200 rounded-2xl animate-pulse"></div>
						</div>
					</div>
				</nav>
			</header>
		);
	}

	return (
		<>
			<motion.header
				initial={{ y: -100, opacity: 0 }}
				animate={{ y: 0, opacity: 1 }}
				transition={{ duration: 0.8, ease: "easeOut" }}
				className="fixed top-0 left-0 right-0 z-50 glass-effect dark:glass-effect-dark transition-all duration-500"
			>
				{/* Modern glassmorphism effect */}
				<div className="absolute inset-0 bg-white/5 dark:bg-gray-800/5" />
				
				<nav className="container mx-auto px-4 relative z-10">
					<div className="flex items-center justify-between h-20 lg:h-24">
						{/* Logo */}
						<Link to="/" className="flex items-center group">
							<motion.div
								whileHover={{ scale: 1.05, rotate: 2 }}
								whileTap={{ scale: 0.95 }}
								className="relative group"
							>
								{/* Simple blue background effect */}
								<div className="absolute inset-0 bg-blue-400 rounded-full opacity-0 group-hover:opacity-20 transition-all duration-500 scale-110 group-hover:scale-100"></div>
								<div className="absolute inset-0 bg-blue-500 rounded-full opacity-0 group-hover:opacity-15 transition-all duration-500 scale-105 group-hover:scale-100"></div>
								{theme === 'dark' ? (
									<img src={logoDark} className="h-16 lg:h-20 relative z-10" alt="Logo" />
								) : (
									<img src={logoLight} className="h-16 lg:h-20 relative z-10" alt="Logo" />
								)}
							</motion.div>
						</Link>

						{/* Desktop Navigation */}
						<div className="hidden lg:flex items-center space-x-1">
							{navItems.map((item) => (
								<Link
									key={item.path}
									to={item.path}
									className={`relative px-6 py-3 text-sm font-medium transition-all duration-300 rounded-2xl group overflow-hidden ${
										location.pathname === item.path
											? 'text-white shadow-lg shadow-blue-500/25' 
											: 'text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-white/50 dark:hover:bg-gray-800/50'
									}`}
								>
									{/* Active indicator background */}
									{location.pathname === item.path && (
										<motion.div
											layoutId="activeTab"
											className="absolute inset-0 bg-blue-600 rounded-2xl"
											initial={false}
											transition={{ type: "spring", stiffness: 500, damping: 30 }}
										/>
									)}
									
									{/* Hover underline effect */}
									{location.pathname !== item.path && (
										<motion.div
											className="absolute bottom-0 left-1/2 w-0 h-0.5 bg-blue-500 group-hover:w-4/5 group-hover:left-[10%] transition-all duration-300"
											initial={false}
										/>
									)}
									
									{/* Text content - always on top */}
									<span className="relative z-10">{item.label}</span>
								</Link>
							))}
						</div>

						{/* Right Section */}
						<div className="flex items-center space-x-3">
							{/* Theme Switcher */}
							<motion.button
								onClick={toggleTheme}
								whileHover={{ scale: 1.1, rotate: 180 }}
								whileTap={{ scale: 0.9 }}
								className="relative p-3 rounded-2xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-blue-800 transition-all duration-300 shadow-md hover:shadow-lg group hover-lift"
								aria-label="Toggle theme"
							>
								{/* Modern solid background effect */}
								<div className="absolute inset-0 bg-blue-400 rounded-2xl opacity-0 group-hover:opacity-20 transition-all duration-500 scale-110 group-hover:scale-100"></div>
								<div className="absolute inset-0 bg-blue-500 rounded-2xl opacity-0 group-hover:opacity-15 transition-all duration-500 scale-105 group-hover:scale-100"></div>
								<AnimatePresence mode="wait">
									{theme === 'dark' ? (
										<motion.div
											key="moon"
											initial={{ rotate: -90, opacity: 0 }}
											animate={{ rotate: 0, opacity: 1 }}
											exit={{ rotate: 90, opacity: 0 }}
											transition={{ duration: 0.3 }}
											className="relative z-10"
										>
											<FiMoon className="w-5 h-5" />
										</motion.div>
									) : (
										<motion.div
											key="sun"
											initial={{ rotate: 90, opacity: 0 }}
											animate={{ rotate: 0, opacity: 1 }}
											exit={{ rotate: -90, opacity: 0 }}
											transition={{ duration: 0.3 }}
											className="relative z-10"
										>
											<FiSun className="w-6 h-6" />
										</motion.div>
									)}
								</AnimatePresence>
							</motion.button>

							{/* Mobile Menu Button */}
							<button
								onClick={toggleMenu}
								className="lg:hidden relative p-3 rounded-2xl bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-blue-100 dark:hover:bg-blue-800 transition-all duration-300 shadow-md hover:shadow-lg group hover-lift"
								aria-label="Toggle menu"
							>
								{/* Modern solid background effect */}
								<div className="absolute inset-0 bg-blue-400 rounded-2xl opacity-0 group-hover:opacity-20 transition-all duration-500 scale-110 group-hover:scale-100"></div>
								<div className="absolute inset-0 bg-blue-500 rounded-2xl opacity-0 group-hover:opacity-15 transition-all duration-500 scale-105 group-hover:scale-100"></div>
								<AnimatePresence mode="wait">
									{showMenu ? (
										<motion.div
											key="close"
											initial={{ rotate: -90, opacity: 0 }}
											animate={{ rotate: 0, opacity: 1 }}
											exit={{ rotate: 90, opacity: 0 }}
											transition={{ duration: 0.3 }}
											className="relative z-10"
										>
											<FiX className="w-6 h-6" />
										</motion.div>
									) : (
										<motion.div
											key="menu"
											initial={{ rotate: 90, opacity: 0 }}
											animate={{ rotate: 0, opacity: 1 }}
											exit={{ rotate: -90, opacity: 0 }}
											transition={{ duration: 0.3 }}
											className="relative z-10"
										>
											<FiMenu className="w-6 h-6" />
										</motion.div>
									)}
								</AnimatePresence>
							</button>
						</div>
					</div>
				</nav>
			</motion.header>

			{/* Mobile Menu */}
			<AnimatePresence>
				{showMenu && (
					<motion.div
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						exit={{ opacity: 0, y: -20 }}
						transition={{ duration: 0.3 }}
						className="fixed top-20 lg:top-24 left-0 right-0 z-40 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm border-b border-gray-200 dark:border-gray-700 lg:hidden"
					>
						<nav className="container mx-auto px-4 py-4">
							<div className="flex flex-col space-y-2">
								{navItems.map((item) => (
									<Link
										key={item.path}
										to={item.path}
										className={`px-4 py-3 text-base font-medium rounded-xl transition-all duration-300 ${
											location.pathname === item.path
												? 'bg-blue-600 text-white shadow-md'
												: 'text-gray-700 dark:text-gray-300 hover:bg-blue-50 dark:hover:bg-gray-800'
										}`}
									>
										{item.label}
									</Link>
								))}
							</div>
						</nav>
					</motion.div>
				)}
			</AnimatePresence>
		</>
	);
};

export default AppHeader;